import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePage from '@dashboard/hooks/usePage';
import { fetcher } from '@dashboard/services/request';
import { pageKey } from '@dashboard/lib/queryKeys';
import { Form, TextInput } from '@dashboard/components/Formik';
import { Label } from '@dashboard/components/Form';
import { useTicketGroupHelpers } from '../useTicketGroupHelpers';
import { useTicketGroups } from '../TicketGroupsContext';
import { fromZonedTime } from 'date-fns-tz';
import { formatDateForInput } from '@/dashboard/services/formatting/date';
import handleAvailabilityBehaviorOnChange from '../handleAvailabilityBehaviorOnChange';
import {
  TicketGroupAvailabilityBehavior,
  TicketGroupAvailabilityStatus,
} from '@/dashboard/enums/tickets';
import * as Yup from 'yup';

export default function TicketGroupSettingsForm({
  ticketGroup,
  onClose,
  formSubmit: formSubmitProp,
}) {
  const { page } = usePage();
  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();
  const { onDirty, totalQuantitySoldInGroup: totalQuantitySold } = useTicketGroupHelpers(
    ticketGroup._id,
  );
  const { state: ticketGroups } = useTicketGroups();

  const allTicketTypesUnsold = ticketGroup.ticketTypes.every(
    ticketType => ticketType.quantitySold === null,
  );

  const defaultAvailabilityBehavior = 'always_available';

  const getSaleStartOnFromReference = triggerTicketGroup => {
    if (triggerTicketGroup == null || page?.eventDetails?.timezone == null) return null;

    const referencedTicket = ticketGroups.find(t => t._id === triggerTicketGroup);

    if (
      referencedTicket?.availabilityBehavior === TicketGroupAvailabilityBehavior.UNTIL_EVENT_STARTS
    ) {
      if (page?.eventDetails?.startDate != null) {
        return formatDateForInput(page.eventDetails.startDate, page?.eventDetails?.timezone);
      }

      return null;
    }

    if (referencedTicket?.saleEndOn != null) {
      return formatDateForInput(referencedTicket.saleEndOn, page?.eventDetails?.timezone);
    }

    return null;
  };

  const editTicketGroupMutation = useMutation({
    mutationFn: values =>
      fetcher(
        ticketGroup?._id
          ? `/sellers/${sellerId}/ticket-groups/${ticketGroup._id}`
          : `/sellers/${sellerId}/ticket-groups`,
        token,
        {
          method: 'POST',
          body: JSON.stringify(values),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
    },
  });

  return (
    <Form
      onDirty={onDirty}
      validationSchema={Yup.object().shape({
        name: Yup.string()
          .trim()
          .required('Group name is required')
          .min(3, 'Group name should be at least 3 characters'),
        triggerTicketGroup: Yup.string().when('availabilityBehavior', {
          is: value =>
            [
              TicketGroupAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
              TicketGroupAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
              TicketGroupAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
            ].includes(value),
          then: Yup.string().nullable().required('Please select a ticket group'),
          otherwise: Yup.string().nullable(),
        }),
        saleStartOn: Yup.string().when('availabilityBehavior', {
          is: TicketGroupAvailabilityBehavior.DATE_TIME_BASED,
          then: Yup.string()
            .nullable()
            .test('date-required', 'A start or end date is required.', function (value) {
              const { saleEndOn } = this.parent;
              // Only show error on saleStartOn if both are empty
              if ((!value || value === '') && (!saleEndOn || saleEndOn === '')) {
                return false;
              }
              return true;
            }),
          otherwise: Yup.string().nullable(),
        }),
        saleEndOn: Yup.string().when('availabilityBehavior', {
          is: TicketGroupAvailabilityBehavior.DATE_TIME_BASED,
          then: Yup.string().nullable(),
          otherwise: Yup.string().nullable(),
        }),
      })}
      inputClassName="text-sm"
      initialValues={{
        name: ticketGroup?.name || '',
        description: ticketGroup?.description || '',
        preselect: {
          enabled: ticketGroup?.preselect?.enabled || false,
          ticketType: ticketGroup?.preselect?.ticketType || null,
          quantity: ticketGroup?.preselect?.quantity || null,
        },
        ticketSelectionType: ticketGroup.ticketSelectionType,
        feeDisplay: ticketGroup?.feeDisplay || 'included',
        capacity: ticketGroup?.capacity === null ? null : ticketGroup.capacity,
        hidden: ticketGroup?.hidden || false,
        reference: ticketGroup?.reference || '',
        availabilityStatus: ticketGroup?.availabilityStatus || 'available',
        saleStartOn: ticketGroup?.saleStartOn
          ? formatDateForInput(ticketGroup?.saleStartOn, page?.eventDetails?.timezone)
          : undefined,
        saleEndOn: ticketGroup?.saleEndOn
          ? formatDateForInput(ticketGroup?.saleEndOn, page?.eventDetails?.timezone)
          : undefined,
        triggerTicketGroup: ticketGroup?.triggerTicketGroup,
        availabilityBehavior: ticketGroup?.availabilityBehavior || defaultAvailabilityBehavior,
        hideWhenSoldOut: ticketGroup?.hideWhenSoldOut,
        hideWhenNotOnSale: ticketGroup?.hideWhenNotOnSale,
        hideWhenScheduled: ticketGroup?.hideWhenScheduled,
        hideWhenUnavailable: ticketGroup?.hideWhenUnavailable,
      }}
      onSubmit={async (values, actions) => {
        try {
          if (
            values.availabilityBehavior === TicketGroupAvailabilityBehavior.DATE_TIME_BASED &&
            (values.saleStartOn == null || values.saleStartOn === '') &&
            (values.saleEndOn == null || values.saleEndOn === '')
          ) {
            values.availabilityBehavior = TicketGroupAvailabilityBehavior.ALWAYS_AVAILABLE;
          }

          if (
            [
              TicketGroupAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
              TicketGroupAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
              TicketGroupAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
            ].includes(values.availabilityBehavior) &&
            (values.triggerTicketGroup == null || values.triggerTicketGroup === '')
          ) {
            values.saleEndOn = null;
            values.saleStartOn = null;
            values.availabilityBehavior = TicketGroupAvailabilityBehavior.ALWAYS_AVAILABLE;
          }

          if (values.preselect.ticketType === '') {
            values.preselect.ticketType = null;
          }

          const data = await editTicketGroupMutation.mutateAsync({
            ...values,
            pageId: page._id,
            triggerTicketGroup: values.triggerTicketGroup === '' ? null : values.triggerTicketGroup,
            saleStartOn: values.saleStartOn
              ? fromZonedTime(values.saleStartOn, page?.eventDetails?.timezone)
              : null,
            saleEndOn: values.saleEndOn
              ? fromZonedTime(values.saleEndOn, page?.eventDetails?.timezone)
              : null,
          });

          actions.resetForm({
            values: {
              name: data.name,
              description: data.description,
              availabilityStatus: data.availabilityStatus,
              preselect: {
                enabled: data.preselect.enabled,
                ticketType: data.preselect.ticketType,
                quantity: data.preselect.quantity,
              },
              ticketSelectionType: data.ticketSelectionType,
              feeDisplay: data.feeDisplay || 'included',
              capacity: data.capacity,
              hidden: data.hidden,
              reference: data.reference,
              saleStartOn: data?.saleStartOn
                ? formatDateForInput(data.saleStartOn, page?.eventDetails?.timezone)
                : undefined,
              saleEndOn: data?.saleEndOn
                ? formatDateForInput(data.saleEndOn, page?.eventDetails?.timezone)
                : undefined,
              triggerTicketGroup: data?.triggerTicketGroup,
              availabilityBehavior: data?.availabilityBehavior || defaultAvailabilityBehavior,
              hideWhenSoldOut: data?.hideWhenSoldOut,
              hideWhenNotOnSale: data?.hideWhenNotOnSale,
              hideWhenScheduled: data?.hideWhenScheduled,
              hideWhenUnavailable: data?.hideWhenUnavailable,
            },
          });

          return {
            data: data,
          };
        } catch (error) {
          return {
            error: error.message,
          };
        }
      }}
      fields={({ values, setFieldValue }) => {
        const availableTicketGroupOptions = ticketGroups
          .filter(t => t._id !== ticketGroup._id)
          .map(t => ({
            label: t.name,
            value: t._id,
          }));

        const isCapacityReached =
          (values.capacity != null &&
            values.capacity != '' &&
            !allTicketTypesUnsold &&
            totalQuantitySold >= values.capacity) ||
          values.capacity === 0;

        return [
          {
            type: 'custom',
            renderField: () => {
              return (
                <div>
                  <Label>Group</Label>
                  <div>
                    <TextInput
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Group name"
                      className="rounded-b-none focus:z-10 !py-[6px] relative text-sm"
                      required
                      border
                      autoComplete="off"
                    />
                    <TextInput
                      id="description"
                      name="description"
                      className="border-t-0 rounded-t-none -mt-px focus:z-10 !py-[6px] relative text-sm"
                      type="text"
                      placeholder="Group description"
                      border
                    />
                  </div>
                </div>
              );
            },
            reduceMargin: true,
          },
          {
            type: 'columns',
            columns: 2,
            gap: 'sm',
            reduceMargin: true,
            fields: [
              {
                name: 'availabilityStatus',
                type: 'select',
                label: 'Sales status',
                tooltip:
                  isCapacityReached &&
                  `Disabled when tickets sold (currently ${totalQuantitySold} sold) exceeds group availability. Increase availability to change group availability status.`,
                options: [
                  {
                    label: 'Select a group availability',
                    value: '',
                    disabled: true,
                  },
                  {
                    label: 'On sale',
                    value: TicketGroupAvailabilityStatus.AVAILABLE,
                    disabled: isCapacityReached,
                  },
                  {
                    label: 'Unavailable',
                    value: TicketGroupAvailabilityStatus.UNAVAILABLE,
                    disabled: isCapacityReached,
                  },
                  {
                    label: 'Sold out',
                    value: TicketGroupAvailabilityStatus.SOLD_OUT,
                  },
                ],
                reduceMargin: true,
              },
              {
                name: 'availabilityBehavior',
                label: 'Sales start',
                type: 'select',
                options: [
                  {
                    label: 'Now until event starts',
                    value: TicketGroupAvailabilityBehavior.UNTIL_EVENT_STARTS,
                  },
                  {
                    label: 'Always',
                    value: TicketGroupAvailabilityBehavior.ALWAYS_AVAILABLE,
                  },
                  {
                    label: 'Date & time',
                    value: TicketGroupAvailabilityBehavior.DATE_TIME_BASED,
                  },
                  {
                    label: 'When another ticket group sale ends',
                    value: TicketGroupAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                  },
                  {
                    label: 'When another ticket group sells out',
                    value: TicketGroupAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
                  },
                  {
                    label: 'When another ticket group sells out or sale ends',
                    value: TicketGroupAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
                  },
                ],
                reduceMargin: true,
                onChange: e => {
                  handleAvailabilityBehaviorOnChange({
                    triggerKey: 'triggerTicketGroup',
                    value: e.target?.value,
                    setFieldValue,
                    page,
                    values,
                    getSaleStartOnFromReference,
                  });
                },
              },
            ],
          },
          {
            name: 'triggerTicketGroup',
            type: 'select',
            label: 'Ticket group',
            options: [
              {
                label: 'Select a ticket group',
                value: '',
              },
              ...availableTicketGroupOptions,
            ],
            visible: values =>
              [
                TicketGroupAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                TicketGroupAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
                TicketGroupAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
              ].includes(values.availabilityBehavior),
            reduceMargin: true,
            onChange: e => {
              if (e.target?.value) {
                const saleStartOn = getSaleStartOnFromReference(e.target?.value);
                if (saleStartOn) {
                  setFieldValue('saleStartOn', saleStartOn);
                } else {
                  setFieldValue('saleStartOn', '');
                }
              }

              if (e.target?.value == '') {
                setFieldValue('saleStartOn', '');
                setFieldValue('saleEndOn', '');
              }

              setFieldValue('triggerTicketGroup', e.target.value);
            },
          },
          {
            type: 'columns',
            columns: 2,
            gap: 'sm',
            reduceMargin: true,
            fields: [
              {
                name: 'saleStartOn',
                label: 'Start sale on',
                placeholder: 'Start sale on',
                type: 'datetime-local',
                reduceMargin: true,
                disabled: values.availabilityBehavior !== 'date_time_based',
              },
              {
                name: 'saleEndOn',
                label: 'End sale on',
                placeholder: 'End sale on',
                type: 'datetime-local',
                reduceMargin: true,
              },
            ],
            visible: values => {
              if (values.availabilityBehavior === TicketGroupAvailabilityBehavior.DATE_TIME_BASED) {
                return true;
              }

              return (
                [
                  TicketGroupAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                  TicketGroupAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
                ].includes(values.availabilityBehavior) &&
                (values.triggerTicketGroup != null || values.triggerTicketGroup != '')
              );
            },
          },
          {
            name: 'saleEndOn',
            label: 'End sale on',
            placeholder: 'End sale on',
            type: 'datetime-local',
            reduceMargin: true,
            visible: values =>
              values.availabilityBehavior ===
                TicketGroupAvailabilityBehavior.AFTER_TICKET_SOLD_OUT &&
              (values.triggerTicketGroup != null || values.triggerTicketGroup != ''),
          },
          {
            type: 'columns',
            columns: 2,
            gap: 'sm',
            fields: [
              {
                name: 'capacity',
                label: 'Quantity available',
                placeholder: 'Quantity available',
                type: 'number',
                reduceMargin: true,
                onChange: e => {
                  setFieldValue('capacity', e.target?.value);
                  const capacity = parseInt(e.target?.value, 10);

                  if (
                    capacity === 0 ||
                    (!isNaN(capacity) &&
                      !allTicketTypesUnsold &&
                      totalQuantitySold >= e.target?.value)
                  ) {
                    setFieldValue('availabilityStatus', 'sold_out');
                  }
                },
              },
              {
                name: 'ticketSelectionType',
                label: 'Ticket selection',
                type: 'select',
                options: [
                  { label: 'Quantity select', value: 'quantity' },
                  { label: 'Multiple select', value: 'multiple' },
                  { label: 'Single select', value: 'single' },
                ],
                reduceMargin: true,
              },
            ],
            reduceMargin: true,
          },
          {
            name: 'feeDisplay',
            label: 'Ticket fee display',
            type: 'select',
            tooltip:
              'Included in price: $102, "Includes $3 booking fee". Shown separately: $99 + $3 booking fee.',
            options: [
              { label: 'Included in price', value: 'included' },
              { label: 'Shown separately', value: 'separate' },
            ],
            reduceMargin: true,
          },
          {
            type: 'disclosure',
            label: 'Additional settings',
            nested: true,
            fields: [
              {
                name: 'preselect.enabled',
                label: 'Preselect ticket',
                type: 'checkbox',
                reduceMargin: true,
              },
              {
                type: 'columns',
                columns: values.ticketSelectionType === 'quantity' ? 2 : 1,
                reduceMargin: true,
                inset: true,
                gap: 'sm',
                visible: values => values.preselect.enabled,
                fields: [
                  {
                    name: 'preselect.ticketType',
                    label: 'Select ticket',
                    type: 'select',
                    options: [
                      {
                        label: 'Select ticket',
                        value: '',
                        default: true,
                      },
                      ...ticketGroup.ticketTypes.map(t => ({
                        label: t.name,
                        value: t._id,
                      })),
                    ],
                    noMargin: true,
                  },
                  {
                    name: 'preselect.quantity',
                    label: 'Preselect quantity',
                    placeholder: 'Quantity',
                    type: 'number',
                    noMargin: true,
                    visible: values => values.ticketSelectionType === 'quantity',
                  },
                ],
              },
              {
                type: 'fieldgroup',
                label: 'Hide manually',
                fields: [
                  {
                    label: 'Hide this ticket group',
                    name: 'hidden',
                    type: 'checkbox',
                    tooltip: 'Hide this ticket group and display it via query parameters',
                  },
                ],
                reduceMargin: true,
              },
              {
                type: 'fieldgroup',
                label: 'Hide automatically when',
                fields: [
                  {
                    label: 'Scheduled',
                    name: 'hideWhenScheduled',
                    type: 'checkbox',
                    tooltip: 'Hide this ticket group when it is scheduled to go on sale',
                  },
                  {
                    label: 'Not on sale',
                    name: 'hideWhenNotOnSale',
                    type: 'checkbox',
                    tooltip:
                      'Hide this ticket group outside its sales dates (before start or after end date)',
                  },
                  {
                    label: 'Unavailable',
                    name: 'hideWhenUnavailable',
                    type: 'checkbox',
                    tooltip: 'Hide this ticket group when it is unavailable',
                  },
                  {
                    label: 'Sold out',
                    name: 'hideWhenSoldOut',
                    type: 'checkbox',
                    tooltip: 'Hide this ticket group when it is sold out',
                  },
                ],
                reduceMargin: true,
              },
              {
                name: 'reference',
                label: 'Reference',
                type: 'text',
                tooltip:
                  'Used to identify this ticket group in Zapier, webhooks, and query parameters.',
                noMargin: true,
              },
            ],
          },
        ];
      }}
      cancelButton={
        formSubmitProp
          ? null
          : {
              label: 'Cancel',
              onClick: onClose,
            }
      }
      formSubmit={formSubmitProp || null}
      formSubmitAlign="end"
    />
  );
}
